import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Subject, catchError, switchMap, tap } from 'rxjs';

import { ProxyWorkflowStatusType } from '../../../core/models/proxy-workflow-status-type';
import { SortDirectionType } from '../../../core/models/sort-direction';
import { Dropdown, DropdownOption } from '../../../shared/components/dropdown/dropdown';
import { IconButtonComponent } from '../../../shared/components/icon-button/icon-button.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import {
  formatDobNumeric,
  formatLocalDate,
  formatLocalTime,
} from '../../../shared/utils/date-time/date-time.utils';
import { PageItem, getPageItems } from '../../../shared/utils/pagination/pagination.utils';
import {
  RequestStatusKind,
  getProxyStatusBadge,
} from '../../../shared/utils/status-badge/status-badge.utils';
import { EMPTY_CELL, buildQueueRowAriaLabel } from '../../../shared/utils/table/table.utils';
import { PatientPortalProxyUserRequest } from '../../models/patient-portal-proxy-user-request';
import { ProxyVerificationsFilterStateService } from '../../services/proxy-verifications-filter-state.service';
import {
  PatientPortalProxyUserRequestsRequest,
  PatientPortalProxyUserRequestsResult,
  PatientPortalProxyUserRequestsService,
} from '../../services/patient-portal-proxy-user-requests.service';
import { maskSsnLastFive } from '../../utils/identifier.utils';

interface ProxyVerification {
  id: number;
  submittedDate: string;
  submittedTime: string;
  patientName: string;
  isMinor: boolean;
  /** Formatted DOB shown beside the Minor chip; null when unknown. */
  patientDob: string | null;
  identifiers: string[];
  caregiverName: string;
  relationship: string;
  statusLabel: string;
  statusKind: RequestStatusKind;
  /** Row announcement; carries the columns (see buildQueueRowAriaLabel). */
  ariaLabel: string;
  /** Announcements for the two email actions, which name the patient. */
  sendAuthEmailAriaLabel: string;
  resendEmailAriaLabel: string;
  /**
   * Pending means the patient still owes email authorization, so the row offers
   * "Send Auth Email". Minors are verified through their guardian and never get
   * the button, even while the status sits at Pending.
   */
  canSendAuthEmail: boolean;
  /** The auth email is out but unconfirmed, so the row offers "Resend Email". */
  canResendEmail: boolean;
}

type StatusFilterOption = DropdownOption<ProxyWorkflowStatusType | null>;
/** `value` is days of history to include; null is "All Time". */
type DateRangeOption = DropdownOption<number | null>;

@Component({
  selector: 'app-proxy-verifications',
  standalone: true,
  imports: [
    Dropdown,
    FormsModule,
    IconComponent,
    IconButtonComponent,
    InputComponent,
    ProgressSpinnerComponent,
  ],
  templateUrl: './proxy-verifications.html',
  styleUrls: ['./proxy-verifications.scss'],
})
export class ProxyVerifications implements OnInit {
  /**
   * Rendered height of a single table row (see .queue-table__row in the scss):
   * 14px padding top/bottom + 33px two-line date cell (18px date + 15px time)
   * + 1px border.
   */
  private static readonly ROW_HEIGHT_PX = 62;
  /**
   * Fixed chrome inside .table-shell that the rows do not get: the column
   * header (9px padding top/bottom + 14px line + 1.5px --bw-thick border ≈ 34px)
   * plus the pagination footer (12px padding top/bottom + a 32px page button +
   * 1px border = 57px). Subtracted from the shell rather than measuring the row
   * container directly, because the shell is the one element that exists in
   * every state, and the first load waits on this measurement (see the
   * measurement effect) — measuring a container that only exists once rows
   * render would leave the queue with nothing to measure and never load. Keep
   * in sync with the header/pagination mixins in styles/partials/_queue-table.scss.
   */
  private static readonly TABLE_CHROME_PX = 91;
  /** Page size used before the table has been measured, and the floor afterwards. */
  private static readonly MIN_PAGE_SIZE = 6;
  /**
   * Minimum characters before a search term is sent to the API. Matches the
   * linking queue: one or two characters match most of a PHI queue, which is
   * indistinguishable from no filter at all but costs a full round trip.
   */
  private static readonly MIN_SEARCH_LENGTH = 3;

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly requestsService = inject(PatientPortalProxyUserRequestsService);
  private readonly filterState = inject(ProxyVerificationsFilterStateService);
  private readonly loadRequestsTrigger$ = new Subject<void>();
  private readonly tableShell = viewChild<ElementRef<HTMLElement>>('tableShell');
  private resizeTimer?: ReturnType<typeof setTimeout>;
  /**
   * Whether the queue has been requested yet this visit. The first request is
   * deliberately deferred until the shell has been measured, so the queue is
   * fetched exactly once per visit — see recalculatePageSize.
   */
  private hasLoadedOnce = false;

  readonly pageSize = signal(ProxyVerifications.MIN_PAGE_SIZE);
  /**
   * An option per open proxy workflow status, in triage order, plus All. Labels
   * come from getProxyStatusBadge rather than being typed here, so an option can
   * never name a status differently from the chip on the rows it filters to.
   *
   * Approved and Denied are deliberately absent: they are settled outcomes
   * rather than work waiting on someone, so the queue offers no dedicated
   * filter for them. Rows in those states still appear (and still carry their
   * Active / Denied chip) under All, so the mapping keeps every status.
   */
  readonly statusFilterOptions: StatusFilterOption[] = [
    { label: 'All', value: null },
    ...[
      ProxyWorkflowStatusType.Pending,
      ProxyWorkflowStatusType.NeedsInfo,
      ProxyWorkflowStatusType.InReview,
    ].map((value) => ({ label: getProxyStatusBadge(value).label, value })),
  ];
  readonly dateRangeOptions: DateRangeOption[] = [
    { label: 'All Time', value: null },
    { label: 'Last 7 Days', value: 7 },
    {
      label: 'Last 30 Days',
      value: ProxyVerificationsFilterStateService.DEFAULT_DATE_RANGE_DAYS,
    },
    { label: 'Last 90 Days', value: 90 },
    { label: 'Last 12 Months', value: 365 },
  ];

  readonly isLoading = signal(true);
  // Filters are backed by a root-provided service so they stay sticky across
  // in-app navigation (e.g. opening a proxy request and coming back), rather
  // than resetting each time this component is re-created. The dropdowns apply
  // (and reload the queue) as soon as they change; only the search term waits
  // for the Filter button — same split as the linking queue's filter bar.
  readonly selectedStatus = this.filterState.statusFilter;
  readonly selectedDateRange = this.filterState.dateRangeDays;
  readonly searchTerm = this.filterState.searchTerm;
  private readonly appliedSearchTerm = this.filterState.appliedSearchTerm;
  readonly currentPage = signal(1);
  readonly allVerifications = signal<ProxyVerification[]>([]);
  readonly totalVerifications = signal(0);
  readonly loadError = signal<string | null>(null);
  readonly hasActiveSearch = computed(() => this.appliedSearchTerm() !== null);

  /**
   * True when the box holds something, but nothing that survives trimming to the
   * minimum length. Applying such a term would silently reload the unfiltered
   * queue under visible search text, so applyFilters rejects it.
   */
  private readonly isSearchTermTooShort = computed(
    () => this.searchTerm().trim().length > 0 && this.getNormalizedSearchTerm() === null,
  );

  /**
   * Set only when the user actually tries to apply a too-short term (Filter
   * click or Enter) — never while they are still typing — and cleared again as
   * soon as the term changes.
   */
  readonly searchValidationError = signal<string | null>(null);

  /**
   * True when any filter narrows the queue. The date range counts: it defaults
   * to Last 30 Days rather than All Time, so an empty result set is genuinely
   * "nothing in this window" and the empty state must say so.
   */
  readonly hasActiveFilters = computed(
    () =>
      this.selectedStatus() !== null || this.selectedDateRange() !== null || this.hasActiveSearch(),
  );

  readonly totalPages = computed(() => {
    const total = this.totalVerifications();
    return total === 0 ? 1 : Math.ceil(total / this.pageSize());
  });

  readonly pageItems = computed<PageItem[]>(() =>
    getPageItems(this.currentPage(), this.totalPages()),
  );

  /**
   * Text for the queue's persistent live region. Reads out the load outcome so
   * screen-reader users hear failures and result counts they would otherwise only
   * see; the region itself stays mounted (see the template).
   */
  readonly queueStatusAnnouncement = computed(() => {
    if (this.isLoading()) {
      return 'Loading verifications';
    }

    if (this.loadError()) {
      return 'Unable to load caregiver and proxy verifications.';
    }

    const total = this.totalVerifications();
    return total === 0
      ? 'No caregiver or proxy verifications match the selected filters.'
      : `${total} caregiver and proxy verifications.`;
  });

  readonly showingSummary = computed(() => {
    const total = this.totalVerifications();

    if (total === 0) {
      return 'Showing 0 of 0 verifications';
    }

    const start = (this.currentPage() - 1) * this.pageSize() + 1;
    const end = Math.min(start + this.allVerifications().length - 1, total);
    return `Showing ${start}–${end} of ${total} verifications`;
  });

  constructor() {
    // Grow the page size to fill the available table height so larger screens
    // show more rows per page. The shell flexes to fill the viewport and is
    // rendered in every state (loading, error, empty, populated), so it can be
    // measured before any rows exist. This effect owns the queue's first load
    // (recalculatePageSize issues it) rather than ngOnInit: the measurement can
    // only run once the view exists, so loading in ngOnInit would fire one
    // PHI-bearing request at the fallback size and a second at the measured
    // size on every visit.
    effect((onCleanup) => {
      const element = this.tableShell()?.nativeElement;
      if (!element) {
        return;
      }

      this.recalculatePageSize(element);

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => this.schedulePageSizeRecalc(element));
      observer.observe(element);
      onCleanup(() => {
        observer.disconnect();
        clearTimeout(this.resizeTimer);
      });
    });
  }

  ngOnInit(): void {
    // The filter is sticky in a root-scoped service, so it can outlive the
    // option list that set it. Drop any status the Status dropdown cannot
    // represent rather than filtering the queue by a value with nothing selected
    // on screen — an invisible filter on a PHI queue hides rows staff expect.
    if (!this.statusFilterOptions.some((option) => option.value === this.selectedStatus())) {
      this.selectedStatus.set(null);
    }

    this.loadRequestsTrigger$
      .pipe(
        tap(() => {
          this.isLoading.set(true);
          this.loadError.set(null);
        }),
        switchMap(() =>
          this.requestsService.getRequests(this.buildRequestsPayload()).pipe(
            catchError(() => {
              this.loadError.set('Unable to load caregiver / proxy verifications right now.');
              this.allVerifications.set([]);
              this.totalVerifications.set(0);
              this.isLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.applyRequestsResult(result);
          this.loadError.set(null);
          this.isLoading.set(false);
        },
      });

    // No load here on purpose: the measurement effect issues the first request
    // once the shell has been measured, so the queue is fetched once per visit
    // at the right page size. isLoading already starts true, so the spinner is
    // on screen from the first render until that request resolves.
  }

  onStatusChange(value: ProxyWorkflowStatusType | null): void {
    if (value === this.selectedStatus()) {
      return;
    }

    this.selectedStatus.set(value);
    this.reloadFromFirstPage();
  }

  onDateRangeChange(value: number | null): void {
    if (value === this.selectedDateRange()) {
      return;
    }

    this.selectedDateRange.set(value);
    this.reloadFromFirstPage();
  }

  /** Typing in the search box never reloads the queue — the Filter button applies it. */
  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    // The error describes a rejected apply attempt; editing the term starts a
    // new one, so don't nag while the user is still typing.
    this.searchValidationError.set(null);
  }

  /**
   * Commit the current search term and reload the queue from page 1 (Filter
   * button or Enter in the search box). The dropdowns have already applied
   * themselves, so this is the point where status, date range, and search text
   * are all reflected in the loaded rows. A term too short to send raises a
   * validation error instead of silently reloading the unfiltered queue.
   */
  applyFilters(): void {
    if (this.isSearchTermTooShort()) {
      this.searchValidationError.set(
        `Enter at least ${ProxyVerifications.MIN_SEARCH_LENGTH} characters to search.`,
      );
      return;
    }

    this.searchValidationError.set(null);
    this.appliedSearchTerm.set(this.getNormalizedSearchTerm());
    this.reloadFromFirstPage();
  }

  clearSearch(): void {
    this.searchValidationError.set(null);

    if (!this.searchTerm()) {
      return;
    }

    this.searchTerm.set('');

    // Clicking the X clears the box; reload without the term when one was applied.
    if (this.appliedSearchTerm() !== null) {
      this.appliedSearchTerm.set(null);
      this.reloadFromFirstPage();
    }
  }

  /** Re-run the current query after a load failure, keeping page and filter. */
  retry(): void {
    this.loadRequests();
  }

  goToPage(page: number): void {
    const nextPage = Math.max(1, Math.min(page, this.totalPages()));
    if (nextPage === this.currentPage()) {
      return;
    }

    this.currentPage.set(nextPage);
    this.loadRequests();
  }

  /** Open the detail screen for a row. Action buttons stop propagation. */
  openRequest(verification: ProxyVerification): void {
    void this.router.navigate(['/requests/proxy-verifications', verification.id]);
  }

  /** Keyboard equivalent of a row click: `role="row"` carries no activation of its own. */
  onRowKeydown(event: KeyboardEvent, verification: ProxyVerification): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    // Only act when the row itself has focus. A keydown on a control inside the
    // row bubbles up here, and calling preventDefault() would swallow that
    // control's own activation and navigate away instead of running its action.
    if (event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    this.openRequest(verification);
  }

  /**
   * Open the email-authorization modal for a pending verification.
   *
   * TODO: the modal is PBI "Proxy Queue - 03" and the admin API has no
   * send-auth-email endpoint yet. The button is wired here so the row action
   * exists; swap the body for the modal + service call once both land.
   */
  sendAuthEmail(_verification: ProxyVerification): void {
    return;
  }

  /**
   * Re-open the email-authorization modal, pre-confirmed for resend.
   *
   * TODO: same dependency as `sendAuthEmail` — PBI "Proxy Queue - 03".
   */
  resendAuthEmail(_verification: ProxyVerification): void {
    return;
  }

  /**
   * Open the manual proxy-add modal.
   *
   * TODO: the modal is PBI "Proxy Queue - 04". The title-row button is wired
   * here so the entry point exists.
   */
  openManualAddProxy(): void {
    return;
  }

  private mapVerification(request: PatientPortalProxyUserRequest): ProxyVerification {
    const badge = getProxyStatusBadge(request.workflowStatusTypeId);
    // The proxy contract declares patientName non-nullable, but the linking
    // queue's patientDisplayName made the same claim and shipped nulls. Until
    // the API team confirms this field, guard it the same way rather than
    // rendering a blank primary cell.
    const patientName = request.patientName || EMPTY_CELL;
    // Suffix for the announcements. An aria-label replaces the element's
    // accessible name, so a nameless row/button says less rather than reading
    // out the cell's em-dash placeholder.
    const forPatient = request.patientName ? ` for ${request.patientName}` : '';
    const identifiers = this.getPatientIdentifiers(request);

    return {
      id: request.patientPortalProxyUserRequestId,
      submittedDate: formatLocalDate(request.createdOnUtc),
      submittedTime: formatLocalTime(request.createdOnUtc),
      patientName,
      isMinor: request.isMinor,
      patientDob: request.patientDateOfBirth ? formatDobNumeric(request.patientDateOfBirth) : null,
      identifiers,
      caregiverName: request.name,
      relationship: request.relationshipToPatient,
      statusLabel: badge.label,
      statusKind: badge.kind,
      ariaLabel: buildQueueRowAriaLabel(`Open proxy request${forPatient}`, [
        request.isMinor ? 'minor' : null,
        identifiers.length > 0 ? identifiers.join(', ') : null,
        request.name ? `proxy user ${request.name}` : null,
        request.relationshipToPatient ? `relationship ${request.relationshipToPatient}` : null,
        `status ${badge.label}`,
      ]),
      sendAuthEmailAriaLabel: `Send authorization email${forPatient}`,
      resendEmailAriaLabel: `Resend authorization email${forPatient}`,
      canSendAuthEmail:
        request.workflowStatusTypeId === ProxyWorkflowStatusType.Pending && !request.isMinor,
      canResendEmail:
        request.workflowStatusTypeId === ProxyWorkflowStatusType.NeedsInfo && !request.isMinor,
    };
  }

  private getPatientIdentifiers(request: PatientPortalProxyUserRequest): string[] {
    const medicalRecordNumber = request.medicalRecordNumber?.trim();
    const lastFiveSocialSecurityNumber = request.patientLastFiveSocialSecurityNumber?.trim();

    return [
      medicalRecordNumber ? `MRN: ${medicalRecordNumber}` : null,
      lastFiveSocialSecurityNumber ? `SSN: ${maskSsnLastFive(lastFiveSocialSecurityNumber)}` : null,
    ].filter((identifier): identifier is string => identifier !== null);
  }

  private reloadFromFirstPage(): void {
    this.currentPage.set(1);
    this.loadRequests();
  }

  private loadRequests(): void {
    this.hasLoadedOnce = true;
    this.loadRequestsTrigger$.next();
  }

  private schedulePageSizeRecalc(element: HTMLElement): void {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.recalculatePageSize(element), 120);
  }

  /**
   * Size the page to the measured shell and load the queue when that changed
   * anything. Also owns the first load of the visit: an element that measures 0
   * (detached, hidden, or a jsdom test) falls back to MIN_PAGE_SIZE rather than
   * leaving the queue unfetched.
   */
  private recalculatePageSize(element: HTMLElement): void {
    const availableHeight = element.clientHeight - ProxyVerifications.TABLE_CHROME_PX;
    const rowsThatFit =
      availableHeight > 0 ? Math.floor(availableHeight / ProxyVerifications.ROW_HEIGHT_PX) : 0;
    const nextPageSize = Math.max(ProxyVerifications.MIN_PAGE_SIZE, rowsThatFit);

    // Read untracked: this runs inside the measurement effect, and tracking
    // pageSize there would make the effect re-run (tearing down and re-creating
    // the ResizeObserver) every time it writes a new size.
    if (nextPageSize === untracked(this.pageSize)) {
      // The size is already right, so a resize has nothing to do — but the very
      // first measurement still owes the visit its one request.
      if (!this.hasLoadedOnce) {
        this.loadRequests();
      }
      return;
    }

    this.pageSize.set(nextPageSize);
    this.currentPage.set(1);
    this.loadRequests();
  }

  /**
   * Lower bound for the request date, or null for "All Time". The API treats a
   * null bound as unbounded, so the option needs no separate flag.
   */
  private getCreatedAfterUtc(): string | null {
    const days = this.selectedDateRange();
    if (days === null) {
      return null;
    }

    const createdAfter = new Date();
    createdAfter.setDate(createdAfter.getDate() - days);
    return createdAfter.toISOString();
  }

  /**
   * The applied search term, trimmed. The API matches it against the patient
   * name and the proxy user's name; anything shorter than MIN_SEARCH_LENGTH
   * sends null rather than a broad prefix match, so the queue comes back
   * unfiltered instead of returning most of it.
   */
  private getNormalizedSearchTerm(): string | null {
    const normalizedSearchTerm = this.searchTerm().trim();
    return normalizedSearchTerm.length >= ProxyVerifications.MIN_SEARCH_LENGTH
      ? normalizedSearchTerm
      : null;
  }

  private buildRequestsPayload(): PatientPortalProxyUserRequestsRequest {
    // The filter bar drives status, date range, and search; the remaining facets
    // the API supports have no control on this page and stay unfiltered.
    return {
      createdAfterUtc: this.getCreatedAfterUtc(),
      searchTerm: this.appliedSearchTerm(),
      relationshipToPatient: null,
      workflowStatusTypeId: this.selectedStatus(),
      assignedToPatientPortalAdminUserId: null,
      unassignedOnly: false,
      sortColumn: 'CreatedOnUtc',
      sortDirection: SortDirectionType.Descending,
      pageNumber: this.currentPage(),
      pageSize: this.pageSize(),
    };
  }

  private applyRequestsResult(result: PatientPortalProxyUserRequestsResult): void {
    this.allVerifications.set(result.requests.map((request) => this.mapVerification(request)));
    this.totalVerifications.set(result.totalRecordCount);
  }
}
